import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

export default function AboutSection() {
  const { t } = useLanguage();

  const focusPoints: string[] = (t('about.focusPoints') as unknown as string[]) || [];

  return (
    <section id="about" className="py-24 px-6 md:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image */}
          <div className="reveal relative">
            <div className="relative aspect-square rounded-full overflow-hidden border-8 border-primary/10 shadow-ocean-lg">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1c5aee969-1775574806699.png"
                alt="Wild-caught seafood sourcing operations - premium quality fish for professional buyers"
                className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-transparent" />
            </div>
            <div className="absolute -top-8 -left-8 w-32 h-32 rounded-full border-4 border-secondary/30 -z-10 animate-rotate-slow" />
            <div className="absolute -bottom-12 -right-12 w-40 h-40 rounded-full border-4 border-accent/30 -z-10" />
            <div className="absolute bottom-8 -right-8 bg-white shadow-ocean-lg rounded-2xl p-6 animate-float">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="BuildingOfficeIcon" size={24} className="text-primary" />
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary">{t('about.topZones')}</div>
                  <div className="text-sm text-muted-foreground">{t('about.fishingZones')}</div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{t('about.richestWaters')}</p>
            </div>
          </div>

          {/* Right - Content */}
          <div className="reveal">
            <div className="mb-8">
              <Icon name="ChatBubbleBottomCenterTextIcon" size={48} className="text-primary/20 mb-6" />
              <span className="inline-block px-4 py-2 bg-primary/10 text-primary text-sm font-semibold rounded-full mb-6">
                {t('about.badge')}
              </span>
              <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                {t('about.title')}
              </h2>
            </div>

            <div className="space-y-6 text-muted-foreground leading-relaxed border-l-4 border-primary pl-6">
              <p className="text-lg">{t('about.description1')}</p>

              <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 my-8">
                <h3 className="font-serif text-xl font-bold text-foreground mb-4">
                  {t('about.missionTitle')}
                </h3>
                <ul className="space-y-2">
                  {focusPoints.map((point, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Icon name="StarIcon" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Key Points */}
            <div className="grid grid-cols-2 gap-6 mt-12">
              <div className="flex items-start gap-3">
                <Icon name="BuildingOfficeIcon" size={24} className="text-primary flex-shrink-0" />
                <div>
                  <div className="font-semibold text-foreground mb-1">{t('about.established')}</div>
                  <div className="text-sm text-muted-foreground">{t('about.establishedValue')}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="TruckIcon" size={24} className="text-primary flex-shrink-0" />
                <div>
                  <div className="font-semibold text-foreground mb-1">{t('about.markets')}</div>
                  <div className="text-sm text-muted-foreground">{t('about.marketsValue')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}